import { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Provider } from "react-redux";
import { store } from "../../redux/store";

export const useGeneratePDF = () => {
  const getFooterPdfText = (pageNum: number, totalPages: number) => {
    return `Page ${pageNum} of ${totalPages} `;
  };

  const isSafariBrowser =
    navigator.userAgent.includes("Safari") &&
    !navigator.userAgent.includes("Chrome");

  const handleSafariPDF = (
    pdf: jsPDF,
    imgData: string,
    canvas: HTMLCanvasElement,
    filename: string,
    isPrintFooter: boolean,
  ) => {
    const totalPages = Math.ceil(canvas.height / pdf.internal.pageSize.height);
    if (isPrintFooter) {
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();
        const yPos = -(i * pdf.internal.pageSize.height);
        pdf.addImage(imgData, "JPEG", 20, yPos, canvas.width, canvas.height);
        // Optional: simple footer text
        const pageText = getFooterPdfText(i + 1, totalPages);
        pdf.setFontSize(12);
        pdf.text(pageText, 20, pdf.internal.pageSize.height - 30);
      }
    }
    pdf.save(filename);
  };

  const handleDefaultPDF = async (
    pdf: jsPDF,
    tempDiv: HTMLDivElement,
    filename: string,
    isPrintFooter: boolean,
  ) => {
    await pdf.html(tempDiv, {
      margin: [16, 0, 16, 0],
      autoPaging: "text",
      html2canvas: {
        allowTaint: true,
        letterRendering: true,
        logging: false,
      },
      callback: (pdf) => {
        if (isPrintFooter) {
          const pageCount = pdf.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            const footer = getFooterPdfText(i, pageCount);
            pdf.setFontSize(12);
            pdf.text(footer, 20, pdf.internal.pageSize.height - 30);
          }
        }
        pdf.save(filename);
      },
    });
  };

  const generatePDF = async (
    reactTemplate: ReactNode,
    filename = "sample.pdf",
    isPrintFooter = false,
  ) => {
    const reactWrapperTemplate = (
      <Provider store={store}>{reactTemplate}</Provider>
    );

    const pdf = new jsPDF({ orientation: "l", format: "a2", unit: "px" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const tempDiv = document.createElement("div");
    document.body.appendChild(tempDiv);
    tempDiv.style.width = `${pageWidth}px`;
    // Render the template into the hidden div
    const root = createRoot(tempDiv);
    await new Promise((resolve) => {
      root.render(reactWrapperTemplate);
      // Allow time for render & layout
      setTimeout(resolve, 100); // adjust delay as needed
    });

    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      allowTaint: true,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/jpeg");

    if (isSafariBrowser) {
      handleSafariPDF(pdf, imgData, canvas, filename, isPrintFooter);
    } else {
      await handleDefaultPDF(pdf, tempDiv, filename, isPrintFooter);
    }
    document.body.removeChild(tempDiv);
  };
  return {
    generatePDF,
  };
};
